import { createBrowserRouter, Navigate } from 'react-router-dom';
import { ErrorBoundary } from '@/components/ErrorBoundary';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';

// Layouts
import { AdminLayout } from '@/components/admin/Layout/AdminLayout';
import { LojaLayout } from '@/components/loja/Layout/LojaLayout';
import { IndiqueGanhe } from '@/pages/loja/Indique';
import { ConviteHandler } from '@/pages/loja/ConviteHandler';

// Pages - Admin
import { Login } from '@/pages/admin/Login';
import { Dashboard } from '@/pages/admin/Dashboard';
import { ListaProdutos } from '@/pages/admin/produtos/ListaProdutos';
import { NovoProduto } from '@/pages/admin/produtos/NovoProduto';
import { EditarProduto } from '@/pages/admin/produtos/EditarProduto';
import { Filamentos } from '@/pages/admin/insumos/Filamentos';
import { Embalagens } from '@/pages/admin/insumos/Embalagens';
import { Maquinas } from '@/pages/admin/insumos/Maquinas';
import { FilaProducao } from '@/pages/admin/producao/FilaProducao';
import { OrdemProducao } from '@/pages/admin/producao/OrdemProducao';
import { PedidoDetalhe } from '@/pages/admin/pedidos/PedidoDetalhe';
import { PedidosHub } from '@/pages/admin/pedidos/PedidosHub';
import { GerenciarCupons } from '@/pages/admin/cupons/GerenciarCupons';
import { Configuracoes } from '@/pages/admin/configuracoes/Configuracoes';
import { RelatorioBi } from '@/pages/admin/financeiro/RelatorioBi';
import { BI } from '@/pages/admin/BI';
import { RankingFidelidade } from '@/pages/admin/fidelidade/RankingFidelidade';
import { Marketplace } from '@/pages/admin/Marketplace';
import { Categorias } from '@/pages/admin/categorias/Categorias';
import { Clientes } from '@/pages/admin/clientes/Clientes';
import { TagsUnboxing } from '@/pages/admin/TagsUnboxing';

// Pages - Loja
import { Home } from '@/pages/loja/Home';
import { Catalogo } from '@/pages/loja/Catalogo';
import { ProdutoDetalhe } from '@/pages/loja/ProdutoDetalhe';
import { Carrinho } from '@/pages/loja/Carrinho';
import { Checkout } from '@/pages/loja/Checkout';
import { Sobre } from '@/pages/loja/Sobre';
import { Contato } from '@/pages/loja/Contato';
import BlogList from '@/pages/blog/BlogList';
import BlogPost from '@/pages/blog/BlogPost';
import BlogManagement from '@/pages/admin/BlogManagement';
import BlogEditor from '@/pages/admin/BlogEditor';
import { PoliticaPrivacidade } from '@/pages/loja/PoliticaPrivacidade';
import { PoliticaCookies } from '@/pages/loja/PoliticaCookies';
import { TermosUso } from '@/pages/loja/TermosUso';
import LoginCliente from '@/pages/loja/LoginCliente';
import { MinhaConta } from '@/pages/loja/MinhaConta';
import { SalaSecreta } from '@/pages/loja/SalaSecreta';

// Pages - Modo Fábrica
import { ModoFabrica } from '@/pages/modo-fabrica/page';
import { Impressao } from '@/pages/modo-fabrica/impressao/page';
import { Qualidade } from '@/pages/modo-fabrica/qualidade/page';
import { Estoque } from '@/pages/modo-fabrica/estoque/page';
import { ApontamentoProducao } from '@/pages/modo-fabrica/apontar/page';

// Route guards
import { PrivateRoute } from './PrivateRoute';
import { PublicRoute } from './PublicRoute';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000, // 5 minutes
      retry: 1,
    },
  },
});

export const router = createBrowserRouter([
  {
    path: '/',
    element: (
      <QueryClientProvider client={queryClient}>
        <LojaLayout />
      </QueryClientProvider>
    ),
    children: [
      { index: true, element: <Home /> },
      { path: 'catalogo', element: <Catalogo /> },
      { path: 'sala-secreta', element: <SalaSecreta /> },
      { path: 'sobre', element: <Sobre /> },
      { path: 'contato', element: <Contato /> },
      { path: 'blog', element: <BlogList /> },
      { path: 'blog/:slug', element: <BlogPost /> },
      { path: 'politica-de-privacidade', element: <PoliticaPrivacidade /> },
      { path: 'politica-de-cookies', element: <PoliticaCookies /> },
      { path: 'termos-de-uso', element: <TermosUso /> },
      { path: 'login', element: <LoginCliente /> },
      { path: 'minha-conta', element: <MinhaConta /> },
      { path: 'produto/:slug', element: <ProdutoDetalhe /> },
      { path: 'carrinho', element: <Carrinho /> },
      { path: 'checkout', element: <Checkout /> },
      { path: 'indique', element: <IndiqueGanhe /> },
      { path: 'convite/:codigo', element: <ConviteHandler /> },
    ],
  },
  {
    path: '/superlogin',
    element: (
      <QueryClientProvider client={queryClient}>
        <ErrorBoundary>
          <PublicRoute />
        </ErrorBoundary>
      </QueryClientProvider>
    ),
    errorElement: <ErrorBoundary><div /></ErrorBoundary>,
    children: [
      { index: true, element: <Login /> },
    ],
  },
  {
    path: '/admin/login',
    element: <Navigate to="/superlogin" replace />,
  },
  {
    path: '/superadmin',
    element: <Navigate to="/admin" replace />,
  },
  {
    path: '/admin',
    element: (
      <QueryClientProvider client={queryClient}>
        <ErrorBoundary>
          <PrivateRoute />
        </ErrorBoundary>
      </QueryClientProvider>
    ),
    errorElement: <ErrorBoundary><div /></ErrorBoundary>,
    children: [
      {
        element: <AdminLayout />,
        children: [
          { index: true, element: <Dashboard /> },
          { path: 'produtos', element: <ListaProdutos /> },
          { path: 'produtos/novo', element: <NovoProduto /> },
          { path: 'produtos/:id/editar', element: <EditarProduto /> },
          { path: 'categorias', element: <Categorias /> },
          { path: 'insumos/filamentos', element: <Filamentos /> },
          { path: 'insumos/embalagens', element: <Embalagens /> },
          { path: 'maquinas', element: <Maquinas /> },
          { path: 'producao', element: <FilaProducao /> },
          { path: 'producao/ordem/:id', element: <OrdemProducao /> },
          { path: 'pedidos', element: <PedidosHub /> },
          { path: 'pedidos/:id', element: <PedidoDetalhe /> },
          { path: 'cupons', element: <GerenciarCupons /> },
          { path: 'bi', element: <BI /> },
          { path: 'fidelidade', element: <RankingFidelidade /> },
          { path: 'marketplaces', element: <Marketplace /> },
          { path: 'unboxing', element: <TagsUnboxing /> },
          { path: 'financeiro', element: <RelatorioBi /> },
          { path: 'blog', element: <BlogManagement /> },
          { path: 'blog/novo', element: <BlogEditor /> },
          { path: 'blog/:id/editar', element: <BlogEditor /> },
          { path: 'clientes', element: <Clientes /> },
          { path: 'insumos', element: <Navigate to="/admin/insumos/filamentos" replace /> },
          { path: 'configuracoes', element: <Configuracoes /> },
        ],
      },
    ],
  },
  {
    path: '/modo-fabrica',
    element: (
      <QueryClientProvider client={queryClient}>
        <PrivateRoute />
      </QueryClientProvider>
    ),
    children: [
      { index: true, element: <ModoFabrica /> },
      { path: 'impressao', element: <Impressao /> },
      { path: 'qualidade', element: <Qualidade /> },
      { path: 'estoque', element: <Estoque /> },
      { path: 'apontamento', element: <ApontamentoProducao /> },
    ],
  },
  {
    path: '*',
    element: <Navigate to="/" replace />,
  },
]);
